import React from "react";
import { TooltipProps } from "recharts";
import styled from "styled-components";
import { Divider } from "Components";
import { BaseDataItem, DataKeys } from "..";
import { capitalize } from "Utils";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

interface Props<T> extends TooltipProps<ValueType, NameType> {
  dataKeys: DataKeys<T>;
  formatter?: (value: number) => string;
}

export const Tooltip = <T extends BaseDataItem>({
  active,
  payload,
  dataKeys,
  formatter,
}: Props<T>) => {
  if (!active || !payload || !payload.length) return null;

  const item = payload[0].payload as T;

  const stats: Record<string, number> = Object.entries(dataKeys).reduce(
    (acc, [key, config]) => {
      const label = config.tooltipLabel || config.legendLabel;
      acc[label] = item[key];
      return acc;
    },
    {} as Record<string, number>,
  );

  return (
    <Container>
      <Date>{capitalize(item.label)}</Date>
      <StyledDivider />
      <Stats>
        {Object.entries(stats).map(([label, value]) => (
          <React.Fragment key={label}>
            <StatLabel>{label}</StatLabel>
            <StatValue>{formatter ? formatter(value) : value}</StatValue>
          </React.Fragment>
        ))}
      </Stats>
    </Container>
  );
};

const Container = styled.div`
  border-radius: 8px;
  background: var(--content-background-color);
  box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.15);
  padding: 10px 0;
  width: 200px;
`;

const Date = styled.span`
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: var(--main-color);
  text-align: center;
  padding-top: 2px;
`;

const StyledDivider = styled(Divider)`
  margin: 7px 0 10px;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: auto max-content;
  column-gap: 10px;
  row-gap: 12px;
  padding: 5px 15px;
  font-size: 14px;
`;

const StatLabel = styled.span`
  color: var(--secondary-text-color);
`;

const StatValue = styled.span`
  color: var(--text-color);
  width: 100%;
  text-align: center;
  font-weight: 500;
`;
